/**Import react sections */
import React, { Fragment, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { useIsMobile } from '../../hooks/useIsMobile'

import DesktopView from './components/OpenDesktopView'
import OpenViewMovil from './componentsMobile/OpenViewMovil'
import Toaster from '../common/Toaster'
import Edit from './Edit'
import WarrantyChecksPanel from '../services/components/WarrantyChecksPanel'
import LateralPanel from '../common/lateralPanel/LateralPanel'

/**Import actions section */
import {
	getSparePartRequest,
	updateNumberTrackingSpareaPart,
	checkShipping,
	setCarrierInformation,
	senEmailBySpartPart,
	updateEquipment,
	saveSparePartComments,
	addFilesSparePartResquest,
	removeFilesInSparePartRequest,
	downloadPackingListReport,
	TRACKING_LOOKUP
} from '../../store/spareParts/sparePartsActions'
import { sparePartShowSelector } from '../../store/spareParts/sparePartsSelectors'
import { isNullOrEmpty } from '../../store/helpers/StringHelper'
import { isDeliveredStatus } from '../../store/helpers/sparePartsStatusColors'
import { toSafeObject } from '../../store/helpers/StateHelper'
import { loadInternalUsers } from '../../store/users/UsersActions'
import { SPARE_PARTS_IMBERA_USA, SPARE_PARTS_IMBERA_MX, PORTAL_ADMINISTRATOR, DISPATCHER, IMBERA_TECHNICIAN } from '../../resources/RolesEnum'
import { getEmailInternalUsersByRole } from '../../store/helpers/UserHelper'

/**
 * Open sparte parts view
 */
const Open = (props) => {
	/**Section hooks */
	const isMobile = useIsMobile(props.isMobile)
	const history = useHistory()
	let match = useRouteMatch()
	const dispatch = useDispatch()
	const isLoading = useSelector((state) => state.spareParts.getIn(['show', 'isLoading']))
	const userProfile = useSelector((state) => toSafeObject(state.session.get('profile')))
	const internalUsers = useSelector((state) => toSafeObject(state.users.get('internalUsers')))
	const isGettingTrackingData = useSelector((state) => state.spareParts.getIn(['show', 'isGettingTrackingData']))
	const carrierData = useSelector((state) => state.spareParts.getIn(['show', 'carrier']))
	const isSaving = useSelector((state) => state.spareParts.getIn(['show', 'isSaving']))
	const token = match.params.token
	const showSpareRequest = useSelector(sparePartShowSelector)
	const [editLateralPanelConfig, setEditLateralPanelConfig] = useState({
		opened: false,
		part: null
	})
	const [identifierSparePart, setIdentifierSparePart] = useState('')
	const [lateralPanelStatusConfig, setLateralPanelStatusConfig] = useState({
		opened: false,
		service: null
	})
	const [lateralPanelWarranty, setLateralPanelWarranty] = useState({
		opened: false,
		equipment: null
	})
	let emailsInternalUsers = getEmailInternalUsersByRole(
		[SPARE_PARTS_IMBERA_USA, PORTAL_ADMINISTRATOR, DISPATCHER, IMBERA_TECHNICIAN],
		internalUsers
	)
	let emailsImberaMX = getEmailInternalUsersByRole([SPARE_PARTS_IMBERA_MX], internalUsers)

	const [toaster, setToaster] = useState({
		open: false,
		variant: null,
		message: null
	})

	useEffect(() => {
		dispatch(getSparePartRequest(token))
	}, [dispatch, token])

	useEffect(() => {
		dispatch(loadInternalUsers())
	}, [dispatch])
	const onChangeLocation = (location) => history.push(location)

	/**
	 * function to on update spare part in equipment
	 * @param {*} sparePart
	 */
	const onUpdateSpartPart = (sparePart, propertyToUpdate) => {
		return dispatch(updateNumberTrackingSpareaPart(token, sparePart, emailsInternalUsers, emailsImberaMX, propertyToUpdate)).catch((error) => {
			console.error('Error update the spare part', error)
			setToaster({
				open: true,
				message: error ? error : 'An error occurred while updating the spare part',
				variant: 'error'
			})
		})
	}

	/**
	 * The carrier status of the line the panel is showing, read from the request rather than from
	 * the copy the panel was opened with. Twin of the one in the sales view, and for the same
	 * reason: the opened copy stops matching reality as soon as the request changes underneath,
	 * so a panel opened before a delivery event arrives would answer "is this delivered?" with a
	 * status the line no longer has, and produce the error toast for a delivered shipment.
	 *
	 * Only the status is taken this way; the panel's editable model still comes from the opened
	 * copy, so a refresh does not discard what the user is editing inside it.
	 */
	const openedPartToken = editLateralPanelConfig.part && editLateralPanelConfig.part.Token
	const openedPartLiveStatus = (() => {
		const lines = (showSpareRequest.open && showSpareRequest.open.SpareParts) || []
		const line = openedPartToken ? lines.find((item) => item.Token === openedPartToken) : null
		return line ? line.Status : editLateralPanelConfig.part && editLateralPanelConfig.part.Status
	})()

	/**
	 * Check shipping by tracking number
	 * @param {*} sparePart
	 */
	const onCheckShipping = (carrierCode, trackingNumber, token) => {
		const part = editLateralPanelConfig.part
		// Read before the lookup: on the found path the line item's status is overwritten with
		// what the carrier just reported, and this question is about the state that made us run
		// the lookup in the first place.
		const wasDelivered = isDeliveredStatus(openedPartLiveStatus)

		return dispatch(checkShipping(carrierCode, trackingNumber.trim(), token)).then((result) => {
			if (result.outcome === TRACKING_LOOKUP.FOUND) {
				// Twin of the sales view: a new object rather than a write into the one already
				// held in state. The panel re-seeds from it either way, because its effect also
				// depends on the carrier data and that changes on every successful lookup.
				setEditLateralPanelConfig({
					...editLateralPanelConfig,
					part: { ...part, Status: result.data.status }
				})
				return
			}

			// A delivered shipment has no details left to fetch: the backend deregisters it from
			// Trackingmore on delivery, so the lookup failing is the ordinary end of the cycle.
			// Announcing it as an error teaches the user to distrust a screen that is working.
			// The panel says so in its Detail View instead.
			if (wasDelivered) return

			setToaster({
				open: true,
				message: `The request was there but there is no information for this tracking number ${trackingNumber.trim()} to update`,
				variant: 'warning'
			})
		})
	}

	const onSendEmailToTechnical = (sparePartToken) => {
		setIdentifierSparePart(sparePartToken)
		return dispatch(senEmailBySpartPart(token, sparePartToken))
			.then((_) => {
				setIdentifierSparePart('')
				setToaster({
					open: true,
					message: 'Send email to technical successfully',
					variant: 'success'
				})
			})
			.catch((error) => {
				setIdentifierSparePart('')
				setToaster({
					open: true,
					message: !isNullOrEmpty(error) ? error : 'Error to send email to technical',
					variant: 'error'
				})
			})
	}

	const onEditLateralPanelConfig = (part) => {
		setEditLateralPanelConfig({
			opened: !editLateralPanelConfig.opened,
			part: part
		})
		dispatch(setCarrierInformation(null))
	}

	/**Open lateral panel for show equipment warranties */
	const openLateralPanelChangeStatus = (spartPart) => {
		setLateralPanelStatusConfig({
			opened: !lateralPanelStatusConfig.opened,
			spartPart: spartPart
		})
	}

	const onUpdateEquipment = (equipmentToken, refrigerationNotCoolFailure, iluminationFailure, doorFailure, comments, faultWarranty) => {
		dispatch(updateEquipment(token, equipmentToken, refrigerationNotCoolFailure, iluminationFailure, doorFailure, comments, faultWarranty)).catch(
			() => {
				console.error('Error to update service')
			}
		)
	}

	/**
	 * Function to update information basic in spare part
	 * @param {*} sparePartRequestsForm
	 */
	const onSaveSparePartRequest = (mapPosition, sparePartRequestsForm) => {
		return dispatch(saveSparePartComments(token, sparePartRequestsForm))
			.then(() => {
				// Nothing to code
				setToaster({
					open: true,
					message: 'Spare part request update successfully',
					variant: 'success'
				})
			})
			.catch((error) => {})
	}

	/**Open lateral panel for show equipment warranties */
	const openPanelWarrantyOnClick = (item) => {
		setLateralPanelWarranty({
			opened: !lateralPanelWarranty.opened,
			equipment: item
		})
	}

	/**Close lateral panel warranty equipment*/
	const onCloseWarrantyClick = () => {
		setLateralPanelWarranty({ opened: false, equipment: null })
	}

	const onAddFilesInSparePartRequest = (files, sparePart) => {
		return dispatch(addFilesSparePartResquest(sparePart.Token, files)).catch((error) => {
			setToaster({
				open: true,
				message: !isNullOrEmpty(error) ? error : 'An error occurred while adding file',
				variant: 'warning'
			})
		})
	}

	const onDownloadPackingListReport = (token) => {
		return dispatch(downloadPackingListReport(token)).catch((error) => {
			setToaster({
				open: true,
				message: !isNullOrEmpty(error) ? error : 'An error occurred while downloading the report',
				variant: 'warning'
			})
		})
	}

	const onRemoveFilesInSparePartRequests = (fileName, sparePart) => {
		return dispatch(removeFilesInSparePartRequest(sparePart.Token, fileName)).catch((error) => {
			setToaster({
				open: true,
				message: !isNullOrEmpty(error) ? error : 'An error occurred while remove file',
				variant: 'warning'
			})
		})
	}

	return (
		<Fragment>
			{isMobile ? (
				<OpenViewMovil
					isMobile={isMobile}
					onChangeLocation={onChangeLocation}
					isLoading={isLoading}
					sparePartRequest={showSpareRequest}
					userProfile={userProfile}
				/>
			) : (
				<DesktopView
					onChangeLocation={onChangeLocation}
					isLoading={isLoading}
					sparePartRequest={showSpareRequest}
					onUpdateSpartPart={onUpdateSpartPart}
					userProfile={userProfile}
					onEditLateralPanelConfig={onEditLateralPanelConfig}
					onSendEmailToTechnical={onSendEmailToTechnical}
					identifierSparePart={identifierSparePart}
					openLateralPanelChangeStatus={openLateralPanelChangeStatus}
					onUpdateEquipment={onUpdateEquipment}
					onShowWarranty={openPanelWarrantyOnClick}
					isSaving={isSaving}
					onSaveSparePartRequest={onSaveSparePartRequest}
					onAddFilesInSparePartRequest={onAddFilesInSparePartRequest}
					onDownloadPackingListReport={onDownloadPackingListReport}
					onRemoveFilesInSparePartRequests={onRemoveFilesInSparePartRequests}
				/>
			)}

			<Edit
				onCloseButtonClick={onEditLateralPanelConfig}
				part={editLateralPanelConfig.part}
				trackingStatus={openedPartLiveStatus}
				onUpdateSpartPart={onUpdateSpartPart}
				userProfile={userProfile}
				status={showSpareRequest.open && showSpareRequest.open.Status}
				editLateralPanelConfig={editLateralPanelConfig}
				carrierData={carrierData}
				onCheckShipping={onCheckShipping}
				isGettingTrackingData={isGettingTrackingData}
				lateralPanelStatusConfig={lateralPanelStatusConfig}
				openLateralPanelChangeStatus={openLateralPanelChangeStatus}
			/>

			{/** Equipment Panel */
			lateralPanelWarranty.opened && (
				<LateralPanel onClose={onCloseWarrantyClick}>
					<WarrantyChecksPanel
						onCloseButtonClick={onCloseWarrantyClick}
						equipment={lateralPanelWarranty.equipment}
						serviceType={showSpareRequest.open ? showSpareRequest.open.CustomerType : ''}
					/>
				</LateralPanel>
			)}

			<Toaster toasterOptions={toaster} onClose={() => setToaster({ open: false })} />
		</Fragment>
	)
}
export default Open
